// src/components/layout/Footer.tsx
import { NavLink } from 'react-router-dom';
import { Facebook, Instagram } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../lib/i18n/LanguageContext';

const Footer = () => {
  const { t, isTransitioning } = useLanguage();

  return (
    <footer className="bg-neutral-800 text-neutral-50 pt-16 pb-8 relative overflow-hidden">
      <motion.div
        key={`footer-${t.common.footer.copyright}`}
        initial={{ opacity: 0.3, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 }}
        className={isTransitioning ? 'pointer-events-none' : ''}
      >
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* 1) Logo & Contact */}
            <div className="space-y-4 text-center md:text-left">
              <div className="flex justify-center md:justify-start items-center">
                <img
                  src="/images/rosso2.png"
                  alt="Rossopomodoro logo"
                  className="h-10 w-auto object-contain"
                />
              </div>
              <div className="space-y-1">
                <p className="text-neutral-300 text-sm leading-snug font-semibold">Rosso ehf.</p>
                <p className="text-neutral-300 text-xs leading-snug">Kt. 610306-0120</p>
              </div>
              <div className="mt-2 space-y-1 text-neutral-300 text-xs leading-snug">
                <a
                  href="https://maps.app.goo.gl/iJ38nHTkanTaXeiS8"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="block hover:opacity-80 transition-opacity"
                >
                  Laugavegi 40a, 101 Reykjavík
                </a>
                <a
                  href="tel:+354-561-0500"
                  data-umami-event="tel-click"
                  data-umami-event-location="footer"
                  className="block hover:opacity-80 transition-opacity"
                >
                  +354 561 0500
                </a>
                <a
                  href="mailto:larusg@simnet.is"
                  data-umami-event="mailto-click"
                  data-umami-event-location="footer"
                  className="block hover:opacity-80 transition-opacity"
                >
                  larusg@simnet.is
                </a>
              </div>
            </div>

            {/* 2) Opening Hours */}
            <div className="flex flex-col items-center text-center">
              <h3 className="font-serif text-lg uppercase tracking-wider text-white mb-4">
                {t.common.footer.hours.title.toUpperCase()}
              </h3>
              <div className="bg-neutral-700/30 backdrop-blur-sm rounded-xl p-6 border border-neutral-600 w-full max-w-sm">
                <div className="flex flex-col divide-y divide-neutral-600">
                  <div className="flex justify-between items-center py-2">
                    <span className="text-neutral-300 text-sm">
                      {t.common.footer.hours.weekdays}
                    </span>
                    <span className="text-white font-medium text-sm">
                      {t.common.footer.hours.weekdaysTime}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-neutral-300 text-sm">{t.common.footer.hours.sunday}</span>
                    <span className="text-white font-medium text-sm">
                      {t.common.footer.hours.sundayTime}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* 3) Follow Us & Quick Links */}
            <div className="flex flex-col items-center md:items-end text-center md:text-right space-y-4">
              <div className="space-y-2">
                <h3 className="font-serif text-lg uppercase tracking-wider text-white">
                  {t.common.footer.social.title.toUpperCase()}
                </h3>
                <div className="flex space-x-4 justify-center md:justify-end">
                  <a
                    href="https://www.facebook.com/rossopomodoro.is/?locale=is_IS"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="text-white hover:text-red-500 transition-colors"
                  >
                    <Facebook size={20} />
                  </a>
                  <a
                    href="https://www.instagram.com/rossopomodoroiceland"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="text-white hover:text-red-500 transition-colors"
                  >
                    <Instagram size={20} />
                  </a>
                  <a
                    href="https://www.tripadvisor.com/Restaurant_Review-g189970-d1888358-Reviews-Rossopomodoro-Reykjavik_Capital_Region.html"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    aria-label="TripAdvisor"
                    className="text-white hover:text-red-500 transition-colors"
                  >
                    <span
                      aria-hidden="true"
                      className="block w-5 h-5 bg-current"
                      style={{
                        WebkitMask: 'url(/brand-tripadvisor.svg) no-repeat center / contain',
                        mask: 'url(/brand-tripadvisor.svg) no-repeat center / contain',
                      }}
                    />
                    <span className="sr-only">TripAdvisor</span>
                  </a>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-serif text-base uppercase tracking-wider text-white">
                  {t.common.footer.quickLinks.title.toUpperCase()}
                </h4>
                <ul className="space-y-1 text-neutral-300 text-sm leading-snug">
                  <li>
                    <NavLink to="/about" className="hover:text-red-500 transition-colors">
                      {t.common.footer.quickLinks.about}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/menu" className="hover:text-red-500 transition-colors">
                      {t.common.footer.quickLinks.menu}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/wine-list" className="hover:text-red-500 transition-colors">
                      {t.common.footer.quickLinks.wineList}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/contact" className="hover:text-red-500 transition-colors">
                      {t.common.footer.quickLinks.contact}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/careers" className="hover:text-red-500 transition-colors">
                      {t.common.footer.quickLinks.careers}
                    </NavLink>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-12 pt-6 border-t border-neutral-700 text-neutral-400 flex flex-col md:flex-row items-center md:justify-between space-y-2 md:space-y-0">
            <p className="text-xs leading-snug">
              © {new Date().getFullYear()} {t.common.footer.copyright}
            </p>
            <p className="text-xs leading-snug">
              {t.common.credit.text}{' '}
              <a
                href="https://litljomi.is/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="hover:text-red-500 transition-colors font-medium"
              >
                {t.common.credit.company}
              </a>
            </p>
          </div>
        </div>
      </motion.div>
    </footer>
  );
};

export default Footer;
